import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';

/**
 * A dropdown component that renders its menu in a portal to escape overflow:hidden containers.
 * Use this instead of Bootstrap Dropdown when the dropdown is inside a scrollable container.
 */
const PortalDropdown = ({ children, menuItems, align = 'end' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const toggleRef = useRef(null);
  const menuRef = useRef(null);

  // Calculate menu position when opened
  useEffect(() => {
    if (isOpen && toggleRef.current) {
      const rect = toggleRef.current.getBoundingClientRect();
      const menuWidth = 180; // Approximate menu width
      
      let left = align === 'end' ? rect.right - menuWidth : rect.left;
      let top = rect.bottom + 4;
      
      // Ensure menu stays within viewport
      if (left < 10) left = 10;
      if (left + menuWidth > window.innerWidth - 10) {
        left = window.innerWidth - menuWidth - 10;
      }
      
      setMenuPosition({ top, left });
    }
  }, [isOpen, align]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        isOpen &&
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        toggleRef.current &&
        !toggleRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (isOpen) setIsOpen(false);
    };
    
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [isOpen]);

  const handleToggle = (e) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const handleItemClick = (onClick) => (e) => {
    e.stopPropagation();
    setIsOpen(false);
    if (onClick) onClick(e);
  };

  const menu = isOpen ? ReactDOM.createPortal(
    <div
      ref={menuRef}
      className="dropdown-menu show portal-dropdown-menu"
      style={{
        position: 'fixed',
        top: menuPosition.top,
        left: menuPosition.left,
        zIndex: 100001,
        minWidth: '160px',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
        borderRadius: '10px',
        border: '1px solid var(--border-color)',
        background: 'var(--background)',
        padding: '6px',
      }}
    >
      {menuItems.map((item, index) => (
        <div key={index} title={item.disabled && item.title ? item.title : ''}>
          <button
            className={`dropdown-item dropdown-option-hover ${item.danger ? 'dropdown-option-delete' : ''} ${item.className || ''}`}
            onClick={item.disabled ? undefined : handleItemClick(item.onClick)}
            style={{
              border: 'none',
              background: 'transparent',
              width: '100%',
              textAlign: 'left',
              opacity: item.disabled ? 0.4 : 1,
              cursor: item.disabled ? 'not-allowed' : 'pointer',
              pointerEvents: item.disabled ? 'none' : 'auto',
            }}
          >
            {item.icon && <i className={item.icon}></i>}
            {item.label}
          </button>
        </div>
      ))}
    </div>,
    document.body
  ) : null;

  return (
    <>
      <div
        ref={toggleRef}
        onClick={handleToggle}
        style={{ cursor: 'pointer', display: 'inline-block' }}
      >
        {children}
      </div>
      {menu}
    </>
  );
};

export default PortalDropdown;
